import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, Clock, Users, Baby, Flower2, Loader2, MessageCircle, Sparkles, CreditCard } from "lucide-react";
import { toast } from "sonner";
import customerApi from "./customerApi";
import { errMsg } from "./hqUtils";

/* HQ — Private Bookings. Read-only view of the customer's private picking
   bookings plus a Message Heritage button. */

const EDGE = "#EFE4CC";
const PLUM = "var(--hq-accent, #6F4A7A)";
const DEEP = "var(--hq-deep, #5A4565)";

const STATUS_LABEL = {
  pending: "Awaiting deposit", deposit_paid: "Deposit paid", confirmed: "Confirmed", paid: "Paid in full",
  cancelled: "Cancelled", completed: "Completed",
};

const STATUS_PILL = {
  pending:      "bg-[#F7E8C8] text-[#7A5107]",
  deposit_paid: "bg-[#EFDCC0] text-[#6B4A12]",
  confirmed:    "bg-[#3E1F46] text-white",
  paid:         "bg-[#1F5B3F] text-white",
  cancelled:    "bg-[#8E2323] text-white",
  completed:    "bg-[#3C2A1E] text-white",
};

const prettyDate = (iso) => iso ? new Date(`${iso}T00:00:00`).toLocaleDateString("en-NZ", { weekday: "long", day: "numeric", month: "long", year: "numeric" }) : "";

export default function HQPrivateBookings() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    customerApi.get("/customer/private-bookings")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(errMsg(e, "Couldn't load your private bookings.")))
      .finally(() => setLoading(false));
  }, []);

  const bookings = data?.bookings || [];

  return (
    <div className="space-y-6" data-testid="hq-private-bookings" style={{ color: DEEP }}>
      <header>
        <div className="text-[10px] tracking-[0.45em] uppercase font-semibold" style={{ color: PLUM }}>✦ On the farm</div>
        <h1 className="text-4xl mt-1.5">Private Bookings</h1>
        <p className="italic opacity-75 text-sm mt-1 max-w-2xl">
          Your private picking on the farm — everything we have on file for it.
        </p>
      </header>

      {loading ? (
        <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin inline" /></div>
      ) : bookings.length === 0 ? (
        <div className="bg-white border border-dashed p-8 text-center" style={{ borderColor: EDGE }} data-testid="hq-private-empty">
          <Flower2 className="w-8 h-8 mx-auto" style={{ color: PLUM }} />
          <div className="text-xl mt-3">No private bookings yet.</div>
          <p className="text-sm italic opacity-70 mt-1">Private Pickings run January to March — two hours in the rows with your own people.</p>
          <Link to="/dahlias/private-pickings" data-testid="hq-private-book-link"
            className="mt-4 inline-flex items-center gap-2 h-11 px-5 bg-[#6E1E3A] text-white text-xs font-semibold tracking-[0.14em] uppercase">
            <Sparkles className="w-4 h-4" /> Book a private picking
          </Link>
        </div>
      ) : bookings.map((b) => (
        <article key={b.id} className="bg-white border p-4 md:p-5" style={{ borderColor: EDGE }} data-testid={`hq-private-booking-${b.id}`}>
          <div className="flex flex-wrap items-baseline justify-between gap-3">
            <div>
              <div className="text-[10px] tracking-[0.25em] uppercase font-semibold" style={{ color: PLUM }}>{b.reference}</div>
              <h2 className="font-serif text-2xl mt-1">{prettyDate(b.date)}</h2>
            </div>
            <span data-testid={`hq-private-status-${b.id}`}
              className={`inline-flex px-3 py-1 rounded-full text-[10px] tracking-[0.16em] uppercase font-bold ${STATUS_PILL[b.status] || STATUS_PILL.pending}`}>
              {STATUS_LABEL[b.status] || b.status}
            </span>
          </div>
          <div className="mt-3 grid gap-x-6 gap-y-1.5 sm:grid-cols-2 text-sm">
            <div className="inline-flex items-center gap-2"><Clock className="w-4 h-4 opacity-60" /> {b.time}{b.evening ? " · evening booking" : ""}</div>
            <div className="inline-flex items-center gap-2"><Users className="w-4 h-4 opacity-60" /> {b.paid_guests} paid guests</div>
            <div className="inline-flex items-center gap-2"><Baby className="w-4 h-4 opacity-60" /> {b.children} children under 12 (free)</div>
            <div className="inline-flex items-center gap-2"><CalendarDays className="w-4 h-4 opacity-60" /> Booked {new Date(b.created_at).toLocaleDateString("en-NZ")}</div>
          </div>
          <div className="mt-3 text-sm">
            <div className="text-[10px] tracking-[0.2em] uppercase font-semibold opacity-70">Extras</div>
            <div>{(b.extras || []).length ? b.extras.map((e) => `${e.label} ($${e.price})`).join(", ") : "None"}</div>
          </div>
          {b.notes && <p className="mt-2 text-sm italic opacity-75">Your notes: {b.notes}</p>}
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t pt-3" style={{ borderColor: EDGE }}>
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase font-semibold opacity-70">Total</div>
              <div className="font-serif text-3xl" data-testid={`hq-private-total-${b.id}`}>NZ${Number(b.total || 0).toFixed(2)}</div>
              <div className="text-sm mt-1" data-testid={`hq-private-deposit-${b.id}`}>
                50% deposit NZ${Number(b.deposit_due ?? (b.total || 0) / 2).toFixed(2)} · balance
                {" "}NZ${Number(b.balance_due ?? (b.total || 0) / 2).toFixed(2)} due 72 hours before
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {data?.payment?.stripe_link && (
                <a href={data.payment.stripe_link} target="_blank" rel="noopener noreferrer" data-testid={`hq-private-pay-${b.id}`}
                  className="inline-flex items-center gap-2 h-11 px-5 bg-[#1F5B3F] text-white text-xs font-semibold tracking-[0.14em] uppercase">
                  <CreditCard className="w-4 h-4" /> Pay by card
                </a>
              )}
              <Link to="/account/contact" data-testid={`hq-private-message-${b.id}`}
                className="inline-flex items-center gap-2 h-11 px-5 bg-[#6E1E3A] text-white text-xs font-semibold tracking-[0.14em] uppercase">
                <MessageCircle className="w-4 h-4" /> Message Heritage
              </Link>
            </div>
          </div>
        </article>
      ))}

      <div className="bg-white border p-4 space-y-2" style={{ borderColor: EDGE }} data-testid="hq-private-terms">
        <div className="text-[10px] tracking-[0.25em] uppercase font-semibold" style={{ color: PLUM }}>Booking &amp; payment</div>
        <ul className="space-y-1 text-sm">
          {(data?.terms || []).map((t) => <li key={t}>· {t}</li>)}
        </ul>
        {data?.payment?.payment_note && <p className="text-sm italic opacity-80">{data.payment.payment_note}</p>}
        {data?.payment?.bank_account && <p className="text-sm">Direct debit: {data.payment.bank_account}</p>}
      </div>

      <p className="text-sm italic p-4 bg-[#F2EAF3] border-l-4 border-[#6E1E3A]" data-testid="hq-private-change-note">
        {data?.change_note || "Please let us know at least 48 hours before your booking if guest numbers, food requirements or extras change."}
      </p>
    </div>
  );
}
